import {
  Datagrid,
  FunctionField,
  List,
  TopToolbar,
  useRefresh,
} from 'react-admin';
import { Box, Button, Paper, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

import type { QueueMetrics } from './types';

const DISPLAY_NAMES: Record<string, string> = {
  pricing: 'Pricing Updates',
  inventory: 'Inventory Updates',
  catalog: 'Catalog Updates',
};

function displayName(record: QueueMetrics): string {
  return DISPLAY_NAMES[record.name] ?? record.name;
}

function formatEstimate(minutes: number | null): string {
  if (minutes == null) return '-';
  if (minutes === 0) return 'Queue empty';
  if (minutes < 1) return '< 1 min';
  return `~${Math.ceil(minutes)} min`;
}

function formatThroughput(value: number | null): string {
  if (value == null) return '-';
  return `${value.toFixed(1)}/min`;
}

const ListActions = () => {
  const refresh = useRefresh();
  return (
    <TopToolbar>
      <Button startIcon={<RefreshIcon />} onClick={() => refresh()}>
        Refresh
      </Button>
    </TopToolbar>
  );
};

const QueueStatusList = () => (
  <List
    resource="shopify-sync"
    actions={<ListActions />}
    pagination={false}
    title="Shopify Sync Queue Status"
  >
    <Typography variant="body2" sx={{ mx: 2, mt: 1, mb: 2 }}>
      When catalog, inventory, or pricing changes are made in Trove, they are queued and synced
      to Shopify. Shopify rate-limits how quickly updates can be sent, so changes may take some
      time to appear — see the Est. Time to Clear column for a rough estimate.
    </Typography>
    <Datagrid bulkActionButtons={false}>
      <FunctionField<QueueMetrics>
        label="Queue"
        render={displayName}
      />
      <FunctionField<QueueMetrics>
        label="Pending"
        render={record => record.error ?? record.messagesPending}
      />
      <FunctionField<QueueMetrics>
        label="Processing"
        render={record => record.error ? '-' : record.messagesProcessing}
      />
      <FunctionField<QueueMetrics>
        label="Throughput"
        render={record => record.error ? '-' : formatThroughput(record.throughputPerMinute)}
      />
      <FunctionField<QueueMetrics>
        label="Est. Time to Clear"
        render={record => record.error ? '-' : formatEstimate(record.estimatedMinutesToClear)}
      />
    </Datagrid>
    <Paper variant="outlined" sx={{ mx: 2, mt: 3, mb: 2, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>Column Guide</Typography>
      <Box component="dl" sx={{ m: 0, '& dt': { fontWeight: 600 }, '& dd': { mb: 1, ml: 0 } }}>
        <dt>Pending</dt>
        <dd>Messages waiting to be processed.</dd>
        <dt>Processing</dt>
        <dd>Messages currently being synced to Shopify.</dd>
        <dt>Throughput</dt>
        <dd>Average messages processed per minute over the last 15 minutes.</dd>
        <dt>Est. Time to Clear</dt>
        <dd>Approximate time to drain the queue at current throughput.</dd>
      </Box>
      {process.env.NODE_ENV === 'development' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Throughput and time estimates rely on CloudWatch metrics, which are only available in deployed environments.
        </Typography>
      )}
    </Paper>
  </List>
);

export default QueueStatusList;
